// DispatcherClient — web 側の dispatcher HTTP API クライアント
// (architecture.md §4 / Phase 4 Task 5b).
//
// Responsibilities:
// - `POST /rooms`                            — 匿名ルーム作成 (admin_token + wsUrl + roomMeta を取得).
// - `GET /rooms/:id/server`                  — ルーム → WS server 解決 (URL 直リンク経由 / 再入室時).
// - `PATCH /admin/rooms/:id`                 — Phase 6 Task 6.6-B: ルーム名 / 公開フラグ patch.
// - `POST /admin/rooms/:id/owner-password`   — Phase 6 Task 6.6-B: 既存 room に password 後付け.
// - `POST /admin/rooms/:id/email`            — Phase 6 Task 6.6-B: 既存 room に email 紐付け開始.
// - `POST /auth/owner-password`              — Phase 6 Task 7: cross-room login (オーナーパスワード経路).
// - `POST /auth/magic-link`                  — Phase 6 Task 7: cross-room login (email magic link 発行).
// - `GET  /auth/verify?token=...`            — Phase 6 Task 7: magic link クリック先の検証.
// - `POST /auth/logout`                      — Phase 6 Task 7: cookie session 削除.
//
// Design notes:
// - 例外は **投げない**: HTTP errors / network errors を outcome union に集約.
//   呼び出し側 (App / SessionProvider) が `if/switch` で UI 分岐できる形.
// - server 側 use case の outcome 名 (`ok` / `not-found` / `no-server-available`
//   / `unavailable` / `error`) と一致させる. workspace 内で完全に同じ shape を
//   共有したいが server パッケージは公開 type entry を持たないため、ここでは
//   **web 側で再定義**する (Phase 6 でルーム作成 UX を rework するときに公開
//   type を切り出すか判断). server 側との drift は presentation 層からは見え
//   ないので、本ファイル + DispatcherClient.test.ts でローカル整合を担保する.
// - `RoomMeta` は server 側 `roomMeta` shape と一致 (camelCase).
// - 余分なフィールドは silently 受け入れる (forward-compat).
// - admin endpoint は `Authorization: Bearer <adminToken>` を options 経由で
//   受け取る. localStorage への保存と取り出しは AdminTokenStore の責務、本
//   モジュールは「どの token を使うか」を呼び出し側に委譲する.

import type { Logger } from '../../application/ports/Logger.js';

/* -----------------------------------------------------------------------------
 * Shared shapes (mirror of server-side `createDispatcherApp.ts`)
 * --------------------------------------------------------------------------- */

/**
 * `rooms.status` のライフサイクル状態 (architecture.md §18.7).
 */
export type RoomStatus = 'active' | 'draining' | 'deleted';

/**
 * Room settings as exposed by the dispatcher (camelCase, scalar fields).
 *
 * Phase 6+ で項目が増える予定. 余分なフィールドは無視するので前方互換.
 * MVP 描画ドメインで使うのは `canvasWidth` / `canvasHeight` のみ.
 */
export interface RoomSettings {
  canvasWidth: number;
  canvasHeight: number;
  watercolorEnabled?: boolean;
  /**
   * entry-preview-members-2026-07-21: 入室画面 (ReJoinScreen) に snapshot
   * プレビューを出すか. settings JSON 由来なので **undefined = On** (既存全
   * ルームにデフォルト On が効く server 側セマンティクスをそのまま透過).
   */
  entryShowPreview?: boolean;
  /** entry-preview-members-2026-07-21: 入室画面に参加者リストを出すか. undefined = On. */
  entryShowMembers?: boolean;
}

/**
 * Dispatcher が返すルームメタ (= `RoomServerLookupResponse.roomMeta` /
 * `CreateRoomResponse.roomMeta`).
 */
export interface RoomMeta {
  name: string;
  settings: RoomSettings;
  status: RoomStatus;
  /**
   * Phase 7 Task 7-5: `whiteboard.clear` を誰でも実行できるか.
   * `false` (default) なら `Perm.CLEAR` 保有者のみ clear 可、`true` なら全員可.
   * MVP では `room.welcome.room.clearOpen` でも別途配信されるので web 側の
   * 主要参照は SessionView 側. 本フィールドは createRoom / lookupRoomServer の
   * 即時応答経路で使う場合のために残す.
   */
  clearOpen: boolean;
  /**
   * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md、
   * entry-password-2026-08 実装スライス): `rooms.entry_pw_hash` が設定済かどうか.
   *
   * `true` のとき visitor での `room.join` は `auth.entryPassword` が必須
   * (protocol `MIN/MAX_ENTRY_PASSWORD_LENGTH`, 1〜64 文字). admin / credential
   * 認証は影響を受けない (打たない).
   *
   * **古い server 応答で欠落した場合は `false` fallback** (forward-compat,
   * `clearOpen` と同じ流儀) — 「あいことば不要」側に倒す (誤って入室不可に
   * ならない安全な既定).
   */
  entryPasswordRequired: boolean;
}

/* -----------------------------------------------------------------------------
 * createRoom outcome
 * --------------------------------------------------------------------------- */

export interface CreateRoomInput {
  /** 1〜128 文字 (trim 済). 未指定なら dispatcher の default ('お絵描きルーム'). */
  name?: string;
  /** 未指定 / 部分指定なら不足分は dispatcher 側で default に埋められる. */
  settings?: Partial<RoomSettings>;
  /**
   * Phase 6 Task 6: 作成と同時にオーナーパスワード登録 (任意).
   * 8〜256 文字 (server 側 `OwnerPasswordSchema` に準拠). UI 側では 8〜128
   * までしか受け付けない (Task 6 確定方針 W) が、wire は server 側 schema に
   * 合わせて 256 を上限として通す.
   */
  ownerPassword?: string;
  /**
   * Phase 6 Task 6: 作成と同時に email 紐付け開始 (任意).
   * server 側 `EmailSchema` (zod `.email().max(320)`) に準拠.
   * `rooms.owner_email` への commit は magic link クリック確定で行う (案 A).
   */
  ownerEmail?: string;
  /**
   * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md): 作成と同時に
   * 設定 (任意). `@oekaki/protocol` の `MIN/MAX_ENTRY_PASSWORD_LENGTH`
   * (1〜64 文字) に準拠. オーナーパスワードとは別物 — 入室するゲストが打つ
   * 合言葉で、オーナー (admin_token 経由) は打たない.
   */
  entryPassword?: string;
}

export type CreateRoomOutcome =
  | {
      outcome: 'ok';
      roomId: string;
      adminToken: string;
      serverId: string;
      /** `${scheme}://${host}:${port}`. */
      wsUrl: string;
      roomMeta: RoomMeta;
      /** Phase 6 Task 2: ownerPassword が body に来て登録できたか. */
      ownerPasswordSet: boolean;
      /**
       * Phase 6 Task 2: ownerEmail が来て magic link を送出したか.
       * `rooms.owner_email` の commit は magic link クリック後 (案 A).
       *
       * **Phase 6 Task 6.5 (確定方針 U)**: server は email 送信失敗でも
       * 200 + `emailVerificationSent: false` を返すように変更済 (500 は返さない).
       * UI 側は false を warning として扱い、後で `/admin/rooms/:id/email` で
       * 再送できる. ownerEmail が指定されていない場合も `false` (= 「email を
       * 要求せず、送出もしなかった」).
       */
      emailVerificationSent: boolean;
    }
  /** 全 server が満員 / draining / down / heartbeat stale. 503 reply. */
  | { outcome: 'no-server-available' }
  /**
   * Phase 6 Task 6: dispatcher rate limit 超過 (429).
   * - IP-based 5/day (`POST /rooms`)
   * - IP 同時所有 20 (createRoomLimitCheck)
   * のいずれかでヒット. 一律「ルーム作成上限に達しました」と扱える形で
   * UI 側に retryAfterMs を渡す.
   */
  | { outcome: 'rate-limited'; retryAfterMs: number }
  /**
   * - `network`     : fetch 失敗 / DNS / TCP refuse / timeout
   * - `invalid`     : 200 だが JSON parse 失敗 / 期待 shape と不一致
   * - `http-status` : 上記以外の不明な status (4xx の 'invalid_body' 含む)
   *
   * Phase 6 Task 6.6-B で `'email-send-failed'` outcome を撤去 (Task 6.5 で
   * server が 500 を返さなくなったため発火しない). ownerEmail 指定時の
   * 失敗は `outcome: 'ok' + emailVerificationSent: false` で表現される.
   */
  | { outcome: 'error'; cause: 'network' | 'invalid' | 'http-status'; status?: number };

/* -----------------------------------------------------------------------------
 * lookupRoomServer outcome
 * --------------------------------------------------------------------------- */

export type LookupRoomServerOutcome =
  | {
      outcome: 'ok';
      serverId: string;
      wsUrl: string;
      roomMeta: RoomMeta;
    }
  /** UUID 不正 (400) も含めて「ルームが見つからない」UX として扱いたい統合 outcome. */
  | { outcome: 'not-found' }
  /** server が割り当てられていない / 割当 server が down (503). */
  | { outcome: 'unavailable'; reason: 'no_server' | 'server_down' | 'unknown' }
  /**
   * Phase 7 Task 7-6: dispatcher が「メンテナンス中」シグナルを返した (503 +
   * `error: 'maintenance'`). `waitUntil` は epoch ms (durationMs 既定で確定可)
   * か null (= 終了時刻未定). UI 側は `ReJoinScreen` でメンテ中バナーを出して
   * 待機させる.
   */
  | { outcome: 'maintenance'; waitUntil: number | null }
  | { outcome: 'error'; cause: 'network' | 'invalid' | 'http-status'; status?: number };

/* -----------------------------------------------------------------------------
 * Phase 6 Task 6.6-B: admin endpoints (PATCH /admin/rooms/:id, owner-password,
 * email).
 *
 * 共通:
 * - 認証は `Authorization: Bearer <adminToken>` (cookie session は Task 7 で
 *   別経路追加). options 経由で受け取る.
 * - エラー shape は backend の HTTP error body を kebab-case outcome にマップ:
 *     400 'invalid_body'    → { outcome: 'invalid-body' }
 *     401 'unauthorized'    → { outcome: 'unauthorized' }
 *     404 'room_not_found'  → { outcome: 'not-found' }   (PATCH のみ)
 *     429 'rate_limited'    → { outcome: 'rate-limited', retryAfterMs }
 *     その他 / network / invalid → { outcome: 'unknown', message? }
 * - 余分な status は `unknown` に潰し、UI 側は「時間を置いて再試行」と表示する.
 * --------------------------------------------------------------------------- */

export interface AdminAuthOptions {
  /** localStorage の admin_token (`AdminTokenStore.get(roomId)`). */
  readonly adminToken: string;
}

export interface PatchRoomBody {
  /** 1..128 字 (server `name: z.string().trim().min(1).max(128)`). */
  readonly name?: string;
  /** Phase 14+ 公開ライブ機能の準備フラグ. */
  readonly isPublic?: boolean;
  /** Phase 7 Task 7-5: `whiteboard.clear` を誰でも実行可にするか. */
  readonly clearOpen?: boolean;
  /** entry-preview-members-2026-07-21: 入室画面に snapshot プレビューを出すか. */
  readonly entryShowPreview?: boolean;
  /** entry-preview-members-2026-07-21: 入室画面に参加者リストを出すか. */
  readonly entryShowMembers?: boolean;
  /**
   * 入室「あいことば」の設定 / 変更 / 解除.
   * - `string` (1〜64 文字) — 設定 / 変更.
   * - `null` — 解除 (`rooms.entry_pw_hash` をクリア).
   * - 省略 — 現状維持.
   */
  readonly entryPassword?: string | null;
}

export type PatchRoomOutcome =
  | {
      outcome: 'ok';
      id: string;
      name: string;
      /** server 側で `null` が来たら `false` に flatten 済 (Task 6.6-A 修正). */
      isPublic: boolean;
      /** Phase 7 Task 7-5: 切替後の clearOpen 状態. */
      clearOpen: boolean;
      /**
       * entry-preview-members-2026-07-21: 切替後の入室画面プレビュー表示状態.
       * server は settings JSON の undefined を **true** に flatten して返す.
       * 旧 server 互換で欠落時はこちらも true fallback (= undefined = On).
       */
      entryShowPreview: boolean;
      /** entry-preview-members-2026-07-21: 切替後の入室画面参加者リスト表示状態. */
      entryShowMembers: boolean;
      /**
       * 入室「あいことば」: PATCH 後の `entry_pw_hash` 有無.
       * 旧 server 応答で欠落した場合は `false` fallback (forward-compat).
       */
      entryPasswordRequired: boolean;
    }
  | { outcome: 'unauthorized' }
  | { outcome: 'not-found' }
  | { outcome: 'invalid-body' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

export type SetOwnerPasswordOutcome =
  | { outcome: 'ok' }
  | { outcome: 'unauthorized' }
  | { outcome: 'invalid-body' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

export type SetOwnerEmailOutcome =
  | {
      outcome: 'ok';
      /**
       * Phase 6 Task 6.5 確定方針 U: 200 でも email 送信失敗は false で表現.
       * UI 側は false なら「メール送信失敗、再送可能」warning を出す.
       */
      emailVerificationSent: boolean;
    }
  | { outcome: 'unauthorized' }
  | { outcome: 'invalid-body' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

/* -----------------------------------------------------------------------------
 * Phase 6 Task 7: public auth endpoints (`/auth/*`).
 *
 * 共通流儀は Task 6.6-B と揃える:
 *   200 → outcome 'ok' (各方法ごとに extra fields)
 *   400 'invalid_body'    → { outcome: 'invalid-body' }
 *   401 'unauthorized'    → { outcome: 'unauthorized' }     (loginByOwnerPassword)
 *   429 'rate_limited'    → { outcome: 'rate-limited', retryAfterMs }
 *   422                   → { outcome: 'unsupported' }      (verifyMagicLink: signup stub)
 *   その他 / network / invalid → { outcome: 'unknown', message? }
 *
 * `verifyMagicLink` の 400 は body の `reason` (`invalid` / `expired` / `used`)
 * に応じて専用 outcome を返す (UX 上 expire ↔ used を別文言で出したい).
 *
 * 認証経路は `credentials: 'include'` で cookie 自動載せ. オーナーパスワード経路
 * の 200 レスポンスには `Set-Cookie: oekaki_admin=...` (room.owner_email が
 * 設定済の場合のみ) が乗ってくる — HttpOnly なので web からは見えないが、次の
 * cross-room API 呼び出しで自動で付く.
 * --------------------------------------------------------------------------- */

export type LoginByOwnerPasswordOutcome =
  | { outcome: 'ok'; roomId: string; adminToken: string }
  | { outcome: 'unauthorized' }
  | { outcome: 'invalid-body' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

/**
 * server `MagicLinkBodySchema` の `purpose`. `bind_email` は
 * `/admin/rooms/:id/email` 経由で発行されるので、UI の `sendMagicLink` 経路では
 * `recovery` (cross-room) と `room_admin` (特定 room の再認証) のみ受理する.
 */
export type MagicLinkPurpose = 'recovery' | 'room_admin';

export type SendMagicLinkOutcome =
  | { outcome: 'ok' }
  | { outcome: 'invalid-body' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

export type VerifyMagicLinkOutcome =
  /** purpose === 'bind_email': cookie session 発行済 + email 紐付け完了. */
  | { outcome: 'ok-bind-email'; roomId: string; email: string }
  /** purpose === 'recovery' | 'room_admin': cookie session + bearer adminToken. */
  | { outcome: 'ok-admin'; roomId: string; adminToken: string }
  | { outcome: 'invalid' }
  | { outcome: 'expired' }
  | { outcome: 'used' }
  /** 422: signup 等 server 側で未実装の purpose. */
  | { outcome: 'unsupported' }
  | { outcome: 'unknown'; message?: string };

export type LogoutOutcome = { outcome: 'ok' } | { outcome: 'unknown'; message?: string };

/* -----------------------------------------------------------------------------
 * Phase 6 Task 8: cross-room admin endpoints
 *
 * `GET /admin/rooms` (cookie session 必須) — cross-room ダッシュボード一覧.
 * `GET    /admin/rooms/:id/credentials`         — credential 一覧 (Bearer or cookie).
 * `POST   /admin/rooms/:id/credentials`         — credential 発行.
 * `DELETE /admin/rooms/:id/credentials/:credId` — credential 削除.
 * `DELETE /admin/rooms/:id`                     — soft-delete (status='deleted').
 *
 * Server side shapes (`AdminRoomSummary` / `CreateCredentialResponse`) は web
 * 側で 1:1 mirror. server パッケージは web に type entry を expose していないので
 * drift は本ファイルと DispatcherClient.test.ts でローカル整合を担保する.
 *
 * 認証経路:
 *  - `listAdminRooms` : cookie session のみ受領 (Task 3 で `/admin/rooms` は cross-room
 *    session 専用、Bearer は許可しない). web 側は `credentials: 'include'` のみ送る.
 *  - 残り 4 つ : `/admin/rooms/:id/*` 系で Bearer (`adminToken`) **or** cookie session の
 *    両対応 (`requireRoomAdminOrEmailSession`). web は呼び出し元 (AdminPage / AdminPage
 *    内の RoomSettingsModal) が adminToken を保有していれば Bearer 経路、無ければ
 *    cookie session 経路にフォールバック.
 * --------------------------------------------------------------------------- */

/**
 * Mirror of server `AdminRoomSummary` (createDispatcherApp.ts).
 * `adminToken` は server が露出していないので web 側型にも乗せない.
 */
export interface AdminRoomSummary {
  readonly id: string;
  readonly name: string;
  readonly settings: RoomSettings;
  readonly status: RoomStatus;
  /** ISO8601. */
  readonly createdAt: string;
  /** ISO8601. */
  readonly lastActiveAt: string;
  /** `owner_pw_hash !== null`. */
  readonly ownerPasswordSet: boolean;
  /** Phase 6 Task 6.6: 公開フラグ. server で `null` → `false` flatten 済. */
  readonly isPublic: boolean;
  /**
   * Phase 7 Task 7-5: `whiteboard.clear` を誰でも実行可にするかの policy.
   * server は notNull boolean を必ず返す.
   */
  readonly clearOpen: boolean;
}

export type ListAdminRoomsOutcome =
  | { outcome: 'ok'; rooms: readonly AdminRoomSummary[] }
  | { outcome: 'unauthorized' }
  | { outcome: 'unknown'; message?: string };

/* -----------------------------------------------------------------------------
 * `POST /admin/admin-tokens/issue` — 2026-05-23 W2 公開前追加
 *
 * cookie session (= `oekaki_admin`) 経由で email 一致ルームの admin_token を
 * 一括 (or `roomIds` 指定で部分) 発行. AdminPage は magic link 検証直後にこの
 * endpoint を叩いて、受け取った tokens を `AdminTokenStore.save()` に保存し、
 * 以降の全画面で従来通り bearer 経路で動かす (= 「メアド登録 = 全ルームの管理権限を
 * 取得する鍵」を成立させる橋渡し endpoint).
 *
 * 認証:
 *   - cookie session のみ受領 (= `requireOwnerEmailSession`, bearer 不受理).
 *   - web 側は `credentials: 'include'` のみ送る. **Authorization ヘッダは送らない**.
 *
 * Rate limit: IP 10/min. 429 時は `Retry-After` ヘッダ + body の `retryAfterMs`
 * を `parseRetryAfter` で拾う.
 * --------------------------------------------------------------------------- */

export interface IssueAdminTokenEntry {
  readonly roomId: string;
  readonly adminToken: string;
}

export type IssueAdminTokensOutcome =
  | { outcome: 'ok'; tokens: readonly IssueAdminTokenEntry[] }
  | { outcome: 'unauthorized' }
  | { outcome: 'rate-limited'; retryAfterMs?: number }
  | { outcome: 'unknown'; message: string };

export interface IssueAdminTokensOptions {
  /**
   * 任意. 指定すると email 一致ルームの中から intersection で filter される
   * (= 不一致は server 側で silently drop). 未指定 / 空配列 = email 一致全ルーム.
   */
  readonly roomIds?: readonly string[];
}

/* -----------------------------------------------------------------------------
 * `POST /admin/rooms/:id/bind-session-email` — 2026-05-23 W2 公開前追加
 *
 * AdminPage の「メアド未紐付けあなたのルーム」セクションのワンクリック紐付け
 * ボタンが叩く endpoint. localStorage の admin_token を保持していて、かつ
 * cookie session で email 確定済のユーザが、対象 room の `owner_email` に自分の
 * session email を書き込む.
 *
 * 認証:
 *   - **cookie session + Bearer admin_token を両方必須**
 *     - cookie session が「どの email に紐付けるか」を決める
 *     - Bearer admin_token が「対象 room を本当に所有しているか」を証明する
 *   - どちらか欠けると 401 (cookie 欠落) or 403 (admin_token 不一致).
 *
 * Outcome 流儀:
 *   200 → { outcome: 'ok', ownerEmail }
 *   401 'unauthorized'  → { outcome: 'unauthorized' }   (cookie session or admin_token 無効)
 *   403 'forbidden'     → { outcome: 'forbidden' }      (admin_token が URL :id と不一致)
 *   404 'room_not_found'→ { outcome: 'not-found' }
 *   409 'conflict'      → { outcome: 'conflict' }       (既に owner_email 紐付け済)
 *   429 'rate_limited'  → { outcome: 'rate-limited', retryAfterMs }
 *   その他 / network / invalid → { outcome: 'unknown', message? }
 * --------------------------------------------------------------------------- */

export interface BindSessionEmailOptions {
  /** localStorage の admin_token (`AdminTokenStore.get(roomId)`). 必須. */
  readonly adminToken: string;
}

export type BindSessionEmailOutcome =
  | { outcome: 'ok'; ownerEmail: string }
  | { outcome: 'unauthorized' }
  | { outcome: 'forbidden' }
  | { outcome: 'not-found' }
  /** 409: 既に owner_email 紐付け済 (= UI 側で section 移動 + 既に紐付き済の旨を表示). */
  | { outcome: 'conflict' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

/**
 * Mirror of server `CreateCredentialResponse` (`GET` and `POST` で同じ shape).
 * `pwHash` は server が射影段階で落とす.
 */
export interface CredentialSummary {
  readonly id: string;
  readonly loginId: string;
  readonly displayName: string | null;
  /** bitflag (0..ALL_PERMS=3). */
  readonly permissions: number;
  /** ISO8601. */
  readonly createdAt: string;
}

/**
 * Auth options for the room-scoped admin endpoints. Bearer **or** cookie
 * session — `adminToken` 未指定なら cookie 経由 (`credentials: 'include'`).
 */
export interface RoomAdminAuthOptions {
  /** localStorage の admin_token (`AdminTokenStore.get(roomId)`). null/undefined なら cookie session 経路. */
  readonly adminToken?: string | null;
}

export type ListCredentialsOutcome =
  | { outcome: 'ok'; credentials: readonly CredentialSummary[] }
  | { outcome: 'unauthorized' }
  | { outcome: 'not-found' }
  | { outcome: 'unknown'; message?: string };

export interface CreateCredentialBody {
  /** 1..32 字 (server `CredentialLoginIdSchema`). */
  readonly loginId: string;
  /** 8..128 字 (server `CredentialPasswordSchema`). */
  readonly password: string;
  /** 1..64 字 or 省略 (server は trim 後 1..64 を要求、空欄なら undefined を送る). */
  readonly displayName?: string;
  /** bitflag 0..ALL_PERMS=3. */
  readonly permissions: number;
}

export type CreateCredentialOutcome =
  | { outcome: 'ok'; credential: CredentialSummary }
  | { outcome: 'unauthorized' }
  | { outcome: 'invalid-body' }
  /** 409 — `(room_id, login_id)` UNIQUE 違反. */
  | { outcome: 'conflict' }
  | { outcome: 'rate-limited'; retryAfterMs: number }
  | { outcome: 'unknown'; message?: string };

export type DeleteCredentialOutcome =
  | { outcome: 'ok' }
  | { outcome: 'unauthorized' }
  | { outcome: 'not-found' }
  | { outcome: 'unknown'; message?: string };

export type DeleteRoomOutcome =
  | { outcome: 'ok' }
  | { outcome: 'unauthorized' }
  | { outcome: 'not-found' }
  | { outcome: 'unknown'; message?: string };

/* -----------------------------------------------------------------------------
 * `POST /admin/rooms/by-tokens` — HomePage 「あなたのルーム」 bulk lookup.
 *
 * (consult 2026-05-20). web 側 (HomePage) は localStorage に保持している
 * `{ roomId, adminToken }[]` をまとめて送り、3 バケツに分類された結果を受け取る:
 *
 *   - `active`  : `rooms` に存在し adminToken が一致した room.
 *   - `deleted` : 物理削除済 (`deleted_rooms` tombstone) で adminToken 一致.
 *   - `invalid` : どちらにも該当しなかった roomId. UI 側で localStorage から
 *                 prune する材料.
 *
 * 認証は **bearerless** (adminToken は body の各 entry 内). rate limit は
 * dispatcher 側で IP 60/h.
 * --------------------------------------------------------------------------- */

/** Tombstone の削除理由. server `DeletionReason` (schema.ts) と 1:1 mirror. */
export type DeletionReason = 'idle_anonymous' | 'idle_password' | 'idle_email' | 'soft_deleted';

export interface ActiveRoomLookupResult {
  readonly roomId: string;
  readonly name: string;
  /** ISO8601. */
  readonly lastActiveAt: string;
}

export interface DeletedRoomLookupResult {
  readonly roomId: string;
  readonly name: string;
  /** ISO8601. */
  readonly deletedAt: string;
  readonly reason: DeletionReason;
}

export type LookupRoomsByTokensOutcome =
  | {
      outcome: 'ok';
      active: readonly ActiveRoomLookupResult[];
      deleted: readonly DeletedRoomLookupResult[];
      invalid: readonly string[];
    }
  | { outcome: 'rate-limited' }
  | { outcome: 'invalid-body' }
  | { outcome: 'network-error'; message: string };

/* -----------------------------------------------------------------------------
 * `GET /maintenance/status` — public maintenance probe (HomePage バナー用).
 *
 * 認証 / roomId 不要・常に 200. body `{ maintenance, waitUntil }`:
 *   - `maintenance: false`              → { outcome: 'open' }
 *   - `maintenance: true`               → { outcome: 'maintenance', waitUntil }
 *     `waitUntil` は再開予定 epoch ms か null (= 再開時刻 不定).
 *   - network / parse / 非200            → { outcome: 'error' }   (fail-silent 用途)
 *
 * lookupRoomServer の maintenance 経路 (503 + `error: 'maintenance'`) は
 * 「特定 room の入室を試みたとき」専用. こちらは HomePage が「そもそも今
 * メンテ中か」を能動的に問い合わせる public probe で、roomId を持たない
 * 段階でも叩ける. 取得失敗時はバナーを出さない (= 主導線を邪魔しない) ので
 * error は単一の sentinel に潰す.
 * --------------------------------------------------------------------------- */

export type MaintenanceStatusOutcome =
  | { outcome: 'open' }
  | { outcome: 'maintenance'; waitUntil: number | null }
  | { outcome: 'error' };

/* -----------------------------------------------------------------------------
 * `GET /rooms/:roomId/entry-info` — public entry preview + members
 * (entry-preview-members-2026-07-21).
 *
 * 入室画面 (ReJoinScreen) が mount 時に 1 回だけ叩く公開 endpoint (認証なし、
 * IP 60/min). server 側 `EntryInfoResponse` の mirror:
 *   - `preview`: **null = `entryShowPreview` Off 専用** (e555dd7 で契約明確化).
 *     On だが snapshot 未生成 (作成直後 / presign 失敗 degrade) は
 *     `{ layerUrls: [], width, height }` (空配列) で届く — UI はプレースホルダ.
 *     `layerUrls` は下層 → 上層の順の presigned layer PNG URL (TTL 60s) で、
 *     `<img>` を source-over で素直に重ねられる. snapshot は 5 分トリガなので
 *     最大 ~5 分古い (UI 側で注記).
 *   - `preview.url`: 入室プレビューの鮮度対処 C (undo-conflict-aware-2026-07-12.md
 *     「入室プレビューの鮮度」節、2026-07-23 確定) で追加された**単枚**合成 PNG
 *     presigned URL (optional、preview-only job 未生成 / 旧 server は欠落 or
 *     null). あれば `layerUrls` を一切 fetch せず単枚 `<img>` で表示 (帯域改善)。
 *     読み込み失敗時のみ `layerUrls` 重ねへフォールバック.
 *   - `members`: `entryShowMembers` Off なら null. On で活性メンバーなしなら [].
 *     displayName のみ (uid / credential は露出しない). join 順の**先頭 20 人
 *     まで** (server `ENTRY_INFO_MEMBERS_MAX`) に cap 済 (8cd4bd2 追補).
 *   - `membersTotal`: cap 適用前の総人数. `members: null` (Off) のときは null.
 *     旧 server (欠落) は null に normalize し、UI 側は members.length で degrade.
 *
 * 入室画面の主目的は入室なので、失敗 (network / 非200 / parse) は全て単一の
 * `{ outcome: 'error' }` sentinel に潰す (getMaintenanceStatus と同じ fail-silent
 * 流儀). UI 側は error でプレビュー枠を出さないだけで入室フォームは通常動作.
 * --------------------------------------------------------------------------- */

export interface EntryInfoPreview {
  /**
   * 下層 → 上層の順の presigned layer PNG URL 群 (TTL 60s).
   * **空配列 = On だが snapshot 未生成** (e555dd7、Off は preview 自体が null).
   * `url` (単枚) がある間は使われない — `url` 読み込み失敗時のフォールバック専用.
   */
  readonly layerUrls: readonly string[];
  /**
   * 入室プレビューの鮮度対処 C で追加された単枚合成 PNG presigned URL
   * (optional). あれば `layerUrls` を fetch せずこちらだけで表示する.
   */
  readonly url?: string;
  /** room settings の canvas 寸法 (px). アスペクト比の維持に使う. */
  readonly width: number;
  readonly height: number;
}

export interface EntryInfoMember {
  readonly displayName: string;
}

export type FetchEntryInfoOutcome =
  | {
      outcome: 'ok';
      preview: EntryInfoPreview | null;
      /** join 順の先頭 20 人まで (server 側 cap). Off なら null. */
      members: readonly EntryInfoMember[] | null;
      /**
       * cap 適用前の総人数 (省略表示「+N人」用). `members: null` (Off) のとき
       * は null. 旧 server (フィールド欠落) も null に normalize される —
       * UI は `membersTotal ?? members.length` で degrade する.
       */
      membersTotal: number | null;
      /**
       * 入室「あいことば」が必要な部屋か. `true` のとき `preview` / `members` は
       * Off と同じ形 (`preview: null` / `members: null` / `membersTotal: null`)
       * で届く — サーバがキャンバス内容・参加者を隠す (入室しないと見えない).
       * 旧 server (フィールド欠落) は `false` fallback (forward-compat、
       * 「あいことば不要」側に倒す安全な既定).
       */
      entryPasswordRequired: boolean;
    }
  | { outcome: 'error' };

/* -----------------------------------------------------------------------------
 * Port
 * --------------------------------------------------------------------------- */

export interface DispatcherClient {
  createRoom(input: CreateRoomInput): Promise<CreateRoomOutcome>;
  lookupRoomServer(roomId: string): Promise<LookupRoomServerOutcome>;
  /**
   * HomePage バナー用 public maintenance probe. 認証不要・常に 200 想定だが、
   * network / parse / 非200 は `{ outcome: 'error' }` に潰して fail-silent.
   */
  getMaintenanceStatus(): Promise<MaintenanceStatusOutcome>;
  /**
   * entry-preview-members-2026-07-21: 入室画面向け公開 entry-info (プレビュー +
   * 参加者一覧). 失敗は `{ outcome: 'error' }` に潰す fail-silent (入室フォーム
   * の動作を阻害しない).
   */
  fetchEntryInfo(roomId: string): Promise<FetchEntryInfoOutcome>;
  /** Phase 6 Task 6.6-B: ルーム名 / 公開フラグ patch (両 optional, 最低 1 必要). */
  patchRoom(
    roomId: string,
    body: PatchRoomBody,
    options: AdminAuthOptions,
  ): Promise<PatchRoomOutcome>;
  /** Phase 6 Task 6.6-B: 既存 room に owner password 後付け. */
  setOwnerPassword(
    roomId: string,
    password: string,
    options: AdminAuthOptions,
  ): Promise<SetOwnerPasswordOutcome>;
  /** Phase 6 Task 6.6-B: 既存 room に email 紐付け開始 (magic link 送信). */
  setOwnerEmail(
    roomId: string,
    email: string,
    options: AdminAuthOptions,
  ): Promise<SetOwnerEmailOutcome>;
  /** Phase 6 Task 7: cross-room login (オーナーパスワード経路). */
  loginByOwnerPassword(input: {
    roomId: string;
    password: string;
  }): Promise<LoginByOwnerPasswordOutcome>;
  /** Phase 6 Task 7: cross-room login (email magic link 発行, 常に 200). */
  sendMagicLink(input: {
    email: string;
    purpose: MagicLinkPurpose;
    roomId?: string;
  }): Promise<SendMagicLinkOutcome>;
  /** Phase 6 Task 7: magic link クリック先で token 検証. */
  verifyMagicLink(token: string): Promise<VerifyMagicLinkOutcome>;
  /** Phase 6 Task 7: cookie session を削除 (常に 204). */
  logout(): Promise<LogoutOutcome>;
  /** Phase 6 Task 8: cookie session 必須 cross-room ルーム一覧. */
  listAdminRooms(): Promise<ListAdminRoomsOutcome>;
  /**
   * 2026-05-23 W2 公開前追加: cookie session 経由で email 一致ルームの adminToken
   * を一括発行. AdminPage 初期化時に呼んで、受け取った tokens を localStorage に
   * 保存し、以降の bearer 経路を有効化する.
   */
  issueAdminTokens(options?: IssueAdminTokensOptions): Promise<IssueAdminTokensOutcome>;
  /**
   * 2026-05-23 W2 公開前追加: 当該 room の `owner_email` に session email を書き込む.
   * cookie session (= 紐付け先 email) + Bearer admin_token (= 所有証明) を両方必須.
   * AdminPage の「メアド未紐付けあなたのルーム」セクションのワンクリック紐付け UI が叩く.
   */
  bindSessionEmail(
    roomId: string,
    options: BindSessionEmailOptions,
  ): Promise<BindSessionEmailOutcome>;
  /** Phase 6 Task 8: 当該 room の credential 一覧 (Bearer or cookie session). */
  listCredentials(roomId: string, options?: RoomAdminAuthOptions): Promise<ListCredentialsOutcome>;
  /** Phase 6 Task 8: credential 発行 (Bearer or cookie session). */
  createCredential(
    roomId: string,
    body: CreateCredentialBody,
    options?: RoomAdminAuthOptions,
  ): Promise<CreateCredentialOutcome>;
  /** Phase 6 Task 8: credential 削除 (Bearer or cookie session). */
  deleteCredential(
    roomId: string,
    credId: string,
    options?: RoomAdminAuthOptions,
  ): Promise<DeleteCredentialOutcome>;
  /** Phase 6 Task 8: room soft-delete (status='deleted'). */
  deleteRoom(roomId: string, options?: RoomAdminAuthOptions): Promise<DeleteRoomOutcome>;
  /**
   * HomePage 「あなたのルーム」 bulk lookup. localStorage に保存された
   * `{ roomId, adminToken }[]` をまとめて送り、active / deleted / invalid の
   * 3 バケツに分類された結果を受け取る (bearerless, IP rate-limited).
   */
  lookupRoomsByTokens(
    entries: ReadonlyArray<{ roomId: string; adminToken: string }>,
  ): Promise<LookupRoomsByTokensOutcome>;
}

/* -----------------------------------------------------------------------------
 * Concrete implementation (fetch-based)
 * --------------------------------------------------------------------------- */

/**
 * Minimal fetch type alias (so tests can pass an in-memory mock without
 * pulling in DOM lib). `globalThis.fetch` is the production default.
 */
export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

export interface CreateDispatcherClientOptions {
  /** dispatcher base URL, e.g. `http://localhost:3000`. Trailing `/` は剥がす. */
  baseUrl: string;
  /** Inject a custom fetch (tests / SSR). Default は `globalThis.fetch`. */
  fetchFn?: FetchFn;
  /** logger — debug / warn 用. 未指定なら no-op. */
  logger?: Logger;
}

const noopLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

/**
 * 200 body の最低限の shape チェック. `outcome: 'invalid'` の判定材料.
 *
 * 余分なフィールドは無視 (forward-compat). 必須フィールドの欠落 / 型不一致
 * のみ false を返す.
 *
 * Phase 6 Task 2: server 側 `CreateRoomResponse` に `ownerPasswordSet` /
 * `emailVerificationSent` (boolean) が追加された. これらは Phase 4 → 6 で
 * 後付けされたフィールドなので、欠落時は `false` で埋めて forward-compat
 * を保つ (server 旧版 / モック相手でも 'ok' を返す).
 */
const isCreateRoomOkBody = (
  raw: unknown,
): raw is {
  roomId: string;
  adminToken: string;
  serverId: string;
  wsUrl: string;
  roomMeta: RoomMeta;
  ownerPasswordSet?: boolean;
  emailVerificationSent?: boolean;
} => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.roomId !== 'string' || r.roomId.length === 0) return false;
  if (typeof r.adminToken !== 'string' || r.adminToken.length === 0) return false;
  if (typeof r.serverId !== 'string' || r.serverId.length === 0) return false;
  if (typeof r.wsUrl !== 'string' || r.wsUrl.length === 0) return false;
  if (!isRoomMeta(r.roomMeta)) return false;
  // ownerPasswordSet / emailVerificationSent は optional (forward-compat).
  // 存在する場合のみ型を厳格化する.
  if (r.ownerPasswordSet !== undefined && typeof r.ownerPasswordSet !== 'boolean') return false;
  if (r.emailVerificationSent !== undefined && typeof r.emailVerificationSent !== 'boolean') {
    return false;
  }
  return true;
};

const isLookupOkBody = (
  raw: unknown,
): raw is { serverId: string; wsUrl: string; roomMeta: RoomMeta } => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.serverId !== 'string' || r.serverId.length === 0) return false;
  if (typeof r.wsUrl !== 'string' || r.wsUrl.length === 0) return false;
  if (!isRoomMeta(r.roomMeta)) return false;
  return true;
};

const isRoomMeta = (raw: unknown): raw is RoomMeta => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string') return false;
  if (typeof r.status !== 'string') return false;
  if (r.status !== 'active' && r.status !== 'draining' && r.status !== 'deleted') return false;
  if (typeof r.settings !== 'object' || r.settings === null) return false;
  const s = r.settings as Record<string, unknown>;
  if (typeof s.canvasWidth !== 'number' || !Number.isFinite(s.canvasWidth)) return false;
  if (typeof s.canvasHeight !== 'number' || !Number.isFinite(s.canvasHeight)) return false;
  // Phase 7 Task 7-5: clearOpen は server (Task 7-5 以降) が常に boolean を返す.
  // ただし forward-compat のため optional 扱いにせず、欠落していたら型ガードで
  // 通しつつ caller 側で `?? false` の defensive default を期待する流儀に倒す.
  // 実際には server 側で notNull boolean に固定 + isAdminRoomSummary では
  // 厳格チェックを入れているので、ここを緩めても security 影響なし.
  if (r.clearOpen !== undefined && typeof r.clearOpen !== 'boolean') return false;
  // 入室あいことば: 欠落 (旧 server) は許容、存在するなら型を厳格化.
  if (r.entryPasswordRequired !== undefined && typeof r.entryPasswordRequired !== 'boolean') {
    return false;
  }
  return true;
};

/**
 * entry-preview-members-2026-07-21: 200 body shape gate for
 * `GET /rooms/:roomId/entry-info`. preview / members はそれぞれ null 可.
 * 余分なフィールドは無視 (forward-compat).
 */
const isEntryInfoBody = (
  raw: unknown,
): raw is {
  preview: {
    layerUrls: string[];
    /** 単枚合成プレビュー (optional、欠落 or null は「単枚なし」). */
    url?: string | null;
    width: number;
    height: number;
  } | null;
  members: Array<{ displayName: string }> | null;
  /** 8cd4bd2 追補. 旧 server 互換で欠落可 (呼び出し側で null に normalize). */
  membersTotal?: number | null;
  /** 入室あいことば要否. 旧 server 互換で欠落可 (呼び出し側で false に normalize). */
  entryPasswordRequired?: boolean;
} => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (r.preview !== null) {
    if (typeof r.preview !== 'object' || r.preview === null) return false;
    const p = r.preview as Record<string, unknown>;
    if (!Array.isArray(p.layerUrls)) return false;
    if (!p.layerUrls.every((u) => typeof u === 'string' && u.length > 0)) return false;
    if (p.url !== undefined && p.url !== null) {
      if (typeof p.url !== 'string' || p.url.length === 0) return false;
    }
    if (typeof p.width !== 'number' || !Number.isFinite(p.width) || p.width <= 0) return false;
    if (typeof p.height !== 'number' || !Number.isFinite(p.height) || p.height <= 0) return false;
  }
  if (r.members !== null) {
    if (!Array.isArray(r.members)) return false;
    const membersOk = r.members.every(
      (m) =>
        typeof m === 'object' &&
        m !== null &&
        typeof (m as Record<string, unknown>).displayName === 'string',
    );
    if (!membersOk) return false;
  }
  // membersTotal: 欠落 (旧 server) / null (Off) / 非負の有限数のみ許容.
  if (r.membersTotal !== undefined && r.membersTotal !== null) {
    if (
      typeof r.membersTotal !== 'number' ||
      !Number.isFinite(r.membersTotal) ||
      r.membersTotal < 0
    ) {
      return false;
    }
  }
  // entryPasswordRequired: 欠落 (旧 server) は許容、存在するなら boolean 必須.
  if (r.entryPasswordRequired !== undefined && typeof r.entryPasswordRequired !== 'boolean') {
    return false;
  }
  return true;
};

/**
 * Phase 6 Task 8: 200 body shape gate for `GET /admin/rooms`.
 * 余分なフィールドは無視 (forward-compat).
 */
const isAdminRoomSummary = (raw: unknown): raw is AdminRoomSummary => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return false;
  if (typeof r.name !== 'string') return false;
  if (typeof r.status !== 'string') return false;
  if (r.status !== 'active' && r.status !== 'draining' && r.status !== 'deleted') return false;
  if (typeof r.createdAt !== 'string' || r.createdAt.length === 0) return false;
  if (typeof r.lastActiveAt !== 'string' || r.lastActiveAt.length === 0) return false;
  if (typeof r.ownerPasswordSet !== 'boolean') return false;
  if (typeof r.isPublic !== 'boolean') return false;
  // Phase 7 Task 7-5: clearOpen は server が常に boolean を返す.
  if (typeof r.clearOpen !== 'boolean') return false;
  if (typeof r.settings !== 'object' || r.settings === null) return false;
  const s = r.settings as Record<string, unknown>;
  if (typeof s.canvasWidth !== 'number' || !Number.isFinite(s.canvasWidth)) return false;
  if (typeof s.canvasHeight !== 'number' || !Number.isFinite(s.canvasHeight)) return false;
  return true;
};

/**
 * Phase 6 Task 8: 200 body shape gate for `GET /admin/rooms/:id/credentials`
 * row + `POST /admin/rooms/:id/credentials` 201 body.
 */
const isCredentialSummary = (raw: unknown): raw is CredentialSummary => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return false;
  if (typeof r.loginId !== 'string' || r.loginId.length === 0) return false;
  if (r.displayName !== null && typeof r.displayName !== 'string') return false;
  if (typeof r.permissions !== 'number' || !Number.isFinite(r.permissions)) return false;
  if (typeof r.createdAt !== 'string' || r.createdAt.length === 0) return false;
  return true;
};

/** `POST /admin/rooms/by-tokens` 200 body の row shape gate. */
const isActiveLookupRow = (raw: unknown): raw is ActiveRoomLookupResult => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.roomId !== 'string' || r.roomId.length === 0) return false;
  if (typeof r.name !== 'string') return false;
  if (typeof r.lastActiveAt !== 'string' || r.lastActiveAt.length === 0) return false;
  return true;
};

const isDeletedLookupRow = (raw: unknown): raw is DeletedRoomLookupResult => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r.roomId !== 'string' || r.roomId.length === 0) return false;
  if (typeof r.name !== 'string') return false;
  if (typeof r.deletedAt !== 'string' || r.deletedAt.length === 0) return false;
  if (
    r.reason !== 'idle_anonymous' &&
    r.reason !== 'idle_password' &&
    r.reason !== 'idle_email' &&
    r.reason !== 'soft_deleted'
  ) {
    return false;
  }
  return true;
};

const parseUnavailableReason = (raw: unknown): 'no_server' | 'server_down' | 'unknown' => {
  if (typeof raw !== 'object' || raw === null) return 'unknown';
  const r = raw as Record<string, unknown>;
  if (r.reason === 'no_server' || r.reason === 'server_down') return r.reason;
  return 'unknown';
};

/**
 * Phase 6 Task 6: 429 から retry-after ms を取り出す.
 *
 * Priority:
 *   1. JSON body の `retryAfterMs` (server 側が常に付与する真値).
 *   2. `Retry-After` ヘッダ (秒) を ms 換算.
 *   3. fallback `60_000` ms (UX が極端に悪化しない保守的な値).
 *
 * `Response#json()` を 1 回だけ呼ぶ前提 (body は 1 回しか読めない).
 */
const parseRetryAfter = async (res: Response): Promise<number> => {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // body 読めなければヘッダ fallback.
  }
  if (typeof body === 'object' && body !== null) {
    const b = body as Record<string, unknown>;
    if (
      typeof b.retryAfterMs === 'number' &&
      Number.isFinite(b.retryAfterMs) &&
      b.retryAfterMs > 0
    ) {
      return b.retryAfterMs;
    }
  }
  const hdr = res.headers.get('retry-after');
  if (hdr !== null) {
    const sec = Number.parseInt(hdr, 10);
    if (Number.isFinite(sec) && sec > 0) return sec * 1_000;
  }
  return 60_000;
};

/**
 * Construct the fetch-based DispatcherClient. Exported as a factory so the
 * composition root can build it once with the env-resolved URL.
 */
export const createDispatcherClient = (opts: CreateDispatcherClientOptions): DispatcherClient => {
  const baseUrl = stripTrailingSlash(opts.baseUrl);
  const fetchFn = opts.fetchFn ?? ((input, init) => globalThis.fetch(input, init));
  const logger = opts.logger ?? noopLogger;

  return {
    async createRoom(input) {
      const body = JSON.stringify({
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.settings !== undefined ? { settings: input.settings } : {}),
        ...(input.ownerPassword !== undefined ? { ownerPassword: input.ownerPassword } : {}),
        ...(input.ownerEmail !== undefined ? { ownerEmail: input.ownerEmail } : {}),
        ...(input.entryPassword !== undefined ? { entryPassword: input.entryPassword } : {}),
      });

      let res: Response;
      try {
        res = await fetchFn(`${baseUrl}/rooms`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body,
          // 既存・将来の同オリジン cookie session (Phase 6 Task 3) を活用する
          // ため credentials を含める. CORS allowlist + `credentials: true` は
          // Phase 6 Task 4 で server 側に設定済.
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('createRoom: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'error', cause: 'network' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('createRoom: 200 with invalid JSON body');
          return { outcome: 'error', cause: 'invalid', status: 200 };
        }
        if (!isCreateRoomOkBody(parsed)) {
          logger.warn('createRoom: 200 body shape mismatch', { parsed });
          return { outcome: 'error', cause: 'invalid', status: 200 };
        }
        return {
          outcome: 'ok',
          roomId: parsed.roomId,
          adminToken: parsed.adminToken,
          serverId: parsed.serverId,
          wsUrl: parsed.wsUrl,
          // Phase 7 Task 7-5: clearOpen が server response から欠落していたら
          // false で埋める (forward-compat、isRoomMeta は optional 扱い).
          // 入室あいことば: entryPasswordRequired も同じ流儀で false fallback.
          roomMeta: {
            ...parsed.roomMeta,
            clearOpen: parsed.roomMeta.clearOpen ?? false,
            entryPasswordRequired: parsed.roomMeta.entryPasswordRequired ?? false,
          },
          ownerPasswordSet: parsed.ownerPasswordSet ?? false,
          emailVerificationSent: parsed.emailVerificationSent ?? false,
        };
      }

      if (res.status === 503) {
        // 503 は dispatcher が「全 server が利用不可」と判断したケース.
        // body の `error` 値 ('server_unavailable') は info ログ程度.
        return { outcome: 'no-server-available' };
      }

      if (res.status === 429) {
        // Phase 6 Task 4: rate limit. body は `{ error: 'rate_limited',
        // retryAfterMs }`, 同時に `Retry-After` (秒) ヘッダ付き. body が読めない
        // 場合は 60 秒の保守的な値で fallback (UX が極端に悪化しない値).
        const retryAfterMs = await parseRetryAfter(res);
        logger.warn('createRoom: rate limited', { retryAfterMs });
        return { outcome: 'rate-limited', retryAfterMs };
      }

      // Phase 6 Task 6.6-B: server は email 送信失敗でも 200 +
      // `emailVerificationSent: false` を返すように変更済 (Task 6.5).
      // 500 は本当に内部エラーのときだけ. 'email-send-failed' outcome は撤去.
      // 400 'invalid_body' / 4xx / 5xx / その他は 'http-status' に集約.
      logger.warn('createRoom: unexpected http status', { status: res.status });
      return { outcome: 'error', cause: 'http-status', status: res.status };
    },

    async lookupRoomServer(roomId) {
      // path 自体は server 側でも UUID validate するので、ここは encode のみ.
      const path = `${baseUrl}/rooms/${encodeURIComponent(roomId)}/server`;
      let res: Response;
      try {
        res = await fetchFn(path, { method: 'GET' });
      } catch (cause) {
        logger.warn('lookupRoomServer: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'error', cause: 'network' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('lookupRoomServer: 200 with invalid JSON body');
          return { outcome: 'error', cause: 'invalid', status: 200 };
        }
        if (!isLookupOkBody(parsed)) {
          logger.warn('lookupRoomServer: 200 body shape mismatch', { parsed });
          return { outcome: 'error', cause: 'invalid', status: 200 };
        }
        return {
          outcome: 'ok',
          serverId: parsed.serverId,
          wsUrl: parsed.wsUrl,
          // Phase 7 Task 7-5: clearOpen が server response から欠落していたら
          // false で埋める (forward-compat). 入室あいことば: entryPasswordRequired
          // も同じ流儀.
          roomMeta: {
            ...parsed.roomMeta,
            clearOpen: parsed.roomMeta.clearOpen ?? false,
            entryPasswordRequired: parsed.roomMeta.entryPasswordRequired ?? false,
          },
        };
      }

      if (res.status === 404) {
        return { outcome: 'not-found' };
      }

      // 400 'invalid_room_id' は「URL の UUID が不正」= ユーザ視点では同じ
      // 「ルームが見つかりません」UX に倒したいので 'not-found' に畳む.
      if (res.status === 400) {
        let parsed: unknown = null;
        try {
          parsed = await res.json();
        } catch {
          // body 読めないなら http-status に倒す.
          return { outcome: 'error', cause: 'http-status', status: 400 };
        }
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          (parsed as Record<string, unknown>).error === 'invalid_room_id'
        ) {
          return { outcome: 'not-found' };
        }
        return { outcome: 'error', cause: 'http-status', status: 400 };
      }

      if (res.status === 503) {
        let parsed: unknown = null;
        try {
          parsed = await res.json();
        } catch {
          // body 読めなくても unavailable シグナルは伝える.
          return { outcome: 'unavailable', reason: 'unknown' };
        }
        // Phase 7 Task 7-6: dispatcher は maintenance window 中だけ 503 +
        // `error: 'maintenance'` を返す. `waitUntil` は epoch ms か null.
        // 旧 503 (`error: 'server_unavailable'`) と区別して maintenance 専用
        // outcome に倒す.
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          (parsed as Record<string, unknown>).error === 'maintenance'
        ) {
          const raw = (parsed as Record<string, unknown>).waitUntil;
          const waitUntil = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
          return { outcome: 'maintenance', waitUntil };
        }
        return { outcome: 'unavailable', reason: parseUnavailableReason(parsed) };
      }

      logger.warn('lookupRoomServer: unexpected http status', { status: res.status });
      return { outcome: 'error', cause: 'http-status', status: res.status };
    },

    async getMaintenanceStatus() {
      const path = `${baseUrl}/maintenance/status`;
      let res: Response;
      try {
        res = await fetchFn(path, { method: 'GET' });
      } catch (cause) {
        logger.warn('getMaintenanceStatus: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'error' };
      }

      if (res.status !== 200) {
        logger.warn('getMaintenanceStatus: unexpected http status', { status: res.status });
        return { outcome: 'error' };
      }

      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        logger.warn('getMaintenanceStatus: 200 with invalid JSON body');
        return { outcome: 'error' };
      }
      if (typeof parsed !== 'object' || parsed === null) {
        return { outcome: 'error' };
      }
      const r = parsed as Record<string, unknown>;
      if (typeof r.maintenance !== 'boolean') {
        logger.warn('getMaintenanceStatus: 200 body shape mismatch', { parsed });
        return { outcome: 'error' };
      }
      if (r.maintenance === false) {
        return { outcome: 'open' };
      }
      // maintenance === true. waitUntil は epoch ms (正の有限数) か null.
      const waitUntil =
        typeof r.waitUntil === 'number' && Number.isFinite(r.waitUntil) && r.waitUntil > 0
          ? r.waitUntil
          : null;
      return { outcome: 'maintenance', waitUntil };
    },

    async fetchEntryInfo(roomId) {
      // 入室画面の副次情報なので fail-silent (getMaintenanceStatus と同じ流儀).
      // 404 (room 不在) も 429 (rate limit) もエラー UI を出さず単に非表示に倒す.
      const path = `${baseUrl}/rooms/${encodeURIComponent(roomId)}/entry-info`;
      let res: Response;
      try {
        res = await fetchFn(path, { method: 'GET' });
      } catch (cause) {
        logger.warn('fetchEntryInfo: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'error' };
      }

      if (res.status !== 200) {
        logger.warn('fetchEntryInfo: unexpected http status', { status: res.status });
        return { outcome: 'error' };
      }

      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        logger.warn('fetchEntryInfo: 200 with invalid JSON body');
        return { outcome: 'error' };
      }
      if (!isEntryInfoBody(parsed)) {
        logger.warn('fetchEntryInfo: 200 body shape mismatch', { parsed });
        return { outcome: 'error' };
      }
      return {
        outcome: 'ok',
        preview:
          parsed.preview === null
            ? null
            : {
                layerUrls: parsed.preview.layerUrls,
                // null (旧 server は欠落 = undefined) は「単枚なし」に normalize.
                url: parsed.preview.url ?? undefined,
                width: parsed.preview.width,
                height: parsed.preview.height,
              },
        members: parsed.members,
        // 旧 server (欠落) は null に normalize (= UI が members.length で degrade).
        membersTotal: parsed.membersTotal ?? null,
        // 旧 server (欠落) は false に normalize (= あいことば不要側の安全な既定).
        entryPasswordRequired: parsed.entryPasswordRequired ?? false,
      };
    },

    /* ---------------------------------------------------------------------
     * Phase 6 Task 6.6-B: admin endpoints
     *
     * 共通ヘルパは関数内に閉じる (signature が三者三様で抽象化メリットが薄い).
     * 各 outcome は status code 起点で判定し、body 詳細は info ログのみ.
     * ------------------------------------------------------------------- */

    async patchRoom(roomId, body, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${options.adminToken}`,
          },
          body: JSON.stringify({
            ...(body.name !== undefined ? { name: body.name } : {}),
            ...(body.isPublic !== undefined ? { isPublic: body.isPublic } : {}),
            // Phase 7 Task 7-5: clearOpen を送る (true / false).
            ...(body.clearOpen !== undefined ? { clearOpen: body.clearOpen } : {}),
            // entry-preview-members-2026-07-21: 入室画面の表示フラグ 2 個.
            ...(body.entryShowPreview !== undefined
              ? { entryShowPreview: body.entryShowPreview }
              : {}),
            ...(body.entryShowMembers !== undefined
              ? { entryShowMembers: body.entryShowMembers }
              : {}),
            // 入室あいことば: string (設定/変更) も null (解除) も明示送信.
            // undefined (省略) だけキーを落とす.
            ...(body.entryPassword !== undefined ? { entryPassword: body.entryPassword } : {}),
          }),
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('patchRoom: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('patchRoom: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          typeof (parsed as Record<string, unknown>).id !== 'string' ||
          typeof (parsed as Record<string, unknown>).name !== 'string' ||
          typeof (parsed as Record<string, unknown>).isPublic !== 'boolean'
        ) {
          logger.warn('patchRoom: 200 body shape mismatch', { parsed });
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as {
          id: string;
          name: string;
          isPublic: boolean;
          // Phase 7 Task 7-5: clearOpen は server (Task 7-5 以降) が常に返すが、
          // 旧 server 互換のため optional として narrow し、欠落時は false fallback.
          clearOpen?: unknown;
          // entry-preview-members-2026-07-21: server は undefined = On を true に
          // flatten して返す. 旧 server 互換の欠落時はこちらも true fallback
          // (= undefined = On のセマンティクスに合わせる. clearOpen の false
          // fallback とは既定値の向きが逆なことに注意).
          entryShowPreview?: unknown;
          entryShowMembers?: unknown;
          // 入室あいことば: 切替後の entry_pw_hash 有無.
          entryPasswordRequired?: unknown;
        };
        const clearOpen = typeof r.clearOpen === 'boolean' ? r.clearOpen : false;
        const entryShowPreview =
          typeof r.entryShowPreview === 'boolean' ? r.entryShowPreview : true;
        const entryShowMembers =
          typeof r.entryShowMembers === 'boolean' ? r.entryShowMembers : true;
        // 旧 server (欠落) は false fallback (= あいことば不要側の安全な既定、
        // clearOpen と同じ向き).
        const entryPasswordRequired =
          typeof r.entryPasswordRequired === 'boolean' ? r.entryPasswordRequired : false;
        return {
          outcome: 'ok',
          id: r.id,
          name: r.name,
          isPublic: r.isPublic,
          clearOpen,
          entryShowPreview,
          entryShowMembers,
          entryPasswordRequired,
        };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 404) return { outcome: 'not-found' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('patchRoom: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async setOwnerPassword(roomId, password, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/owner-password`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${options.adminToken}`,
          },
          body: JSON.stringify({ password }),
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('setOwnerPassword: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) return { outcome: 'ok' };
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('setOwnerPassword: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async setOwnerEmail(roomId, email, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/email`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${options.adminToken}`,
          },
          body: JSON.stringify({ email }),
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('setOwnerEmail: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('setOwnerEmail: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          typeof (parsed as Record<string, unknown>).emailVerificationSent !== 'boolean'
        ) {
          logger.warn('setOwnerEmail: 200 body shape mismatch', { parsed });
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as { emailVerificationSent: boolean };
        return { outcome: 'ok', emailVerificationSent: r.emailVerificationSent };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('setOwnerEmail: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    /* ---------------------------------------------------------------------
     * Phase 6 Task 7: public auth endpoints (`/auth/*`).
     *
     * `loginByOwnerPassword` は backend の enumeration 防止に従い「room 不在 /
     * password 不一致 / hash 不在」を全て 401 で返してくる. UI 側もそのまま
     * `unauthorized` 一本で扱う (room の存在と password の正誤を区別しない).
     *
     * `sendMagicLink` は backend が **常に 200** を返す (該当無しでも送信した
     * かのように振る舞う). UI も 200 == 'ok' として「メール確認のお願い」
     * splash に進む.
     *
     * `verifyMagicLink` は 400 body の `reason` で expired / used / invalid を
     * 区別する. 422 は `signup` 等 backend が未対応の purpose に来る.
     *
     * `logout` は 204 (= No Content) で fetch 上は body 無し成功. cookie は
     * Set-Cookie で消されるので web 側の追加処理は不要.
     * ------------------------------------------------------------------- */

    async loginByOwnerPassword(input) {
      const path = `${baseUrl}/auth/owner-password`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ roomId: input.roomId, password: input.password }),
          // owner_email が紐付いた room なら Set-Cookie が付くので credentials
          // を含めて自動で受け取る (HttpOnly のため web からは見えない).
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('loginByOwnerPassword: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('loginByOwnerPassword: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          typeof (parsed as Record<string, unknown>).roomId !== 'string' ||
          typeof (parsed as Record<string, unknown>).adminToken !== 'string'
        ) {
          logger.warn('loginByOwnerPassword: 200 body shape mismatch', { parsed });
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as { roomId: string; adminToken: string };
        return { outcome: 'ok', roomId: r.roomId, adminToken: r.adminToken };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('loginByOwnerPassword: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async sendMagicLink(input) {
      const path = `${baseUrl}/auth/magic-link`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            email: input.email,
            purpose: input.purpose,
            ...(input.roomId !== undefined ? { roomId: input.roomId } : {}),
          }),
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('sendMagicLink: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      // backend は常に 200 を返す (enumeration 防止). body は `{ ok: true }` だが
      // 検査せず status だけで判定する (forward-compat: 余分なフィールド無視).
      if (res.status === 200) return { outcome: 'ok' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('sendMagicLink: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async verifyMagicLink(token) {
      // `xhr=1` は「SPA が投げた verify fetch」の明示マーカー. nginx (本番/staging)
      // はこのクエリで dispatcher へ振り分ける. メールリンクのクリック (= 遷移、
      // `?token=` のみ) やメールスキャナの先読みはマーカーが無いので SPA 着地ページ
      // を返す → token を消費しない. Sec-Fetch-Dest に依存しないため全ブラウザで動く
      // (= Safari 等が fetch に Sec-Fetch-Dest を付けない問題の恒久対策).
      // dispatcher 側の VerifyQuerySchema は非 strict で `xhr` を無視する (= ローカルは
      // nginx を通らず直に届くが token のみ採用).
      const params = new URLSearchParams({ token, xhr: '1' });
      const path = `${baseUrl}/auth/verify?${params.toString()}`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'GET',
          // verify 成功時に Set-Cookie が乗るので credentials を含めて受け取る.
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('verifyMagicLink: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('verifyMagicLink: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (typeof parsed !== 'object' || parsed === null) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as Record<string, unknown>;
        if (r.purpose === 'bind_email') {
          if (typeof r.roomId !== 'string' || typeof r.email !== 'string') {
            return { outcome: 'unknown', message: 'invalid response body' };
          }
          return { outcome: 'ok-bind-email', roomId: r.roomId, email: r.email };
        }
        if (r.purpose === 'recovery' || r.purpose === 'room_admin') {
          if (typeof r.roomId !== 'string' || typeof r.adminToken !== 'string') {
            return { outcome: 'unknown', message: 'invalid response body' };
          }
          return { outcome: 'ok-admin', roomId: r.roomId, adminToken: r.adminToken };
        }
        logger.warn('verifyMagicLink: unknown purpose', { parsed });
        return { outcome: 'unknown', message: 'unknown purpose' };
      }

      if (res.status === 400) {
        // body の `reason` で expired / used / invalid を識別.
        let parsed: unknown = null;
        try {
          parsed = await res.json();
        } catch {
          // body 読めなければ invalid に倒す.
          return { outcome: 'invalid' };
        }
        if (typeof parsed === 'object' && parsed !== null) {
          const reason = (parsed as Record<string, unknown>).reason;
          if (reason === 'expired') return { outcome: 'expired' };
          if (reason === 'used') return { outcome: 'used' };
        }
        return { outcome: 'invalid' };
      }

      if (res.status === 422) return { outcome: 'unsupported' };
      if (res.status === 404) {
        // server 側 'room_not_found' (verify 中に room が消えた等). UX 上は
        // invalid と同じ扱いで OK (再試行不可 = 「リンクが無効」).
        return { outcome: 'invalid' };
      }
      logger.warn('verifyMagicLink: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async logout() {
      const path = `${baseUrl}/auth/logout`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('logout: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }
      if (res.status === 204) return { outcome: 'ok' };
      // 200 等 backend が将来 body 付きで返した場合も 'ok' に倒す (forward-compat).
      if (res.status === 200) return { outcome: 'ok' };
      logger.warn('logout: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    /* ---------------------------------------------------------------------
     * Phase 6 Task 8: cross-room admin endpoints
     *
     * `listAdminRooms` は cookie session 専用 (Bearer は 401). 残り 4 endpoint は
     * Bearer or cookie 両対応 — `options.adminToken` があれば Authorization に
     * 載せ、無ければ cookie 任せ (`credentials: 'include'`).
     * ------------------------------------------------------------------- */

    async listAdminRooms() {
      const path = `${baseUrl}/admin/rooms`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'GET',
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('listAdminRooms: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('listAdminRooms: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (typeof parsed !== 'object' || parsed === null) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as Record<string, unknown>;
        if (!Array.isArray(r.rooms)) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        // server 側で shape 揃ってる前提だが、念のため row 単位 gate.
        const rooms: AdminRoomSummary[] = [];
        for (const row of r.rooms) {
          if (!isAdminRoomSummary(row)) {
            logger.warn('listAdminRooms: skipping malformed row', { row });
            continue;
          }
          rooms.push(row);
        }
        return { outcome: 'ok', rooms };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      logger.warn('listAdminRooms: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async issueAdminTokens(options) {
      const path = `${baseUrl}/admin/admin-tokens/issue`;
      // body 仕様 (server `IssueAdminTokensBodySchema`):
      //   - `{}` ・ `{ roomIds: string[] }` を受理. 未指定 / 空配列は「全件」.
      // server 側は body 無しでも 200 を返すが、Content-Type を明示しないと
      // server の `application/json` check に乗らず空 body 扱いになる. ここでは
      // 「options なしでも明示的に `{}` を送る」流儀に倒し、roomIds 指定時のみ
      // 載せる. Authorization ヘッダは **送らない** (= cookie session 専用).
      const body: { roomIds?: readonly string[] } =
        options?.roomIds !== undefined ? { roomIds: options.roomIds } : {};
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
          credentials: 'include',
        });
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        logger.warn('issueAdminTokens: network error', { err: message });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('issueAdminTokens: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (typeof parsed !== 'object' || parsed === null) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as Record<string, unknown>;
        if (!Array.isArray(r.tokens)) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        // row 単位 gate (forward-compat: 不正 row は黙って skip).
        const tokens: IssueAdminTokenEntry[] = [];
        for (const row of r.tokens) {
          if (typeof row !== 'object' || row === null) {
            logger.warn('issueAdminTokens: skipping malformed row', { row });
            continue;
          }
          const e = row as Record<string, unknown>;
          if (typeof e.roomId !== 'string' || e.roomId.length === 0) {
            logger.warn('issueAdminTokens: skipping row with bad roomId', { row });
            continue;
          }
          if (typeof e.adminToken !== 'string' || e.adminToken.length === 0) {
            logger.warn('issueAdminTokens: skipping row with bad adminToken', { row });
            continue;
          }
          tokens.push({ roomId: e.roomId, adminToken: e.adminToken });
        }
        return { outcome: 'ok', tokens };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      // 400 invalid_body も含めて unknown に集約 (UI 側は「再試行してください」と
      // 表示するだけで分岐しない).
      logger.warn('issueAdminTokens: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async bindSessionEmail(roomId, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/bind-session-email`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          // body は無し (cookie session が email を保持、admin_token が所有を保証).
          // ただし server の `application/json` check に乗らないと
          // 0-length body が許容されない可能性があるため Content-Type は明示.
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${options.adminToken}`,
          },
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('bindSessionEmail: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('bindSessionEmail: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          typeof (parsed as Record<string, unknown>).ownerEmail !== 'string' ||
          ((parsed as Record<string, unknown>).ownerEmail as string).length === 0
        ) {
          logger.warn('bindSessionEmail: 200 body shape mismatch', { parsed });
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as { ownerEmail: string };
        return { outcome: 'ok', ownerEmail: r.ownerEmail };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 403) return { outcome: 'forbidden' };
      if (res.status === 404) return { outcome: 'not-found' };
      if (res.status === 409) return { outcome: 'conflict' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('bindSessionEmail: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async listCredentials(roomId, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/credentials`;
      const headers: Record<string, string> = {};
      if (options?.adminToken !== undefined && options.adminToken !== null) {
        headers.authorization = `Bearer ${options.adminToken}`;
      }
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('listCredentials: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('listCredentials: 200 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (typeof parsed !== 'object' || parsed === null) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const r = parsed as Record<string, unknown>;
        if (!Array.isArray(r.credentials)) {
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        const credentials: CredentialSummary[] = [];
        for (const row of r.credentials) {
          if (!isCredentialSummary(row)) {
            logger.warn('listCredentials: skipping malformed row', { row });
            continue;
          }
          credentials.push(row);
        }
        return { outcome: 'ok', credentials };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 404) return { outcome: 'not-found' };
      logger.warn('listCredentials: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async createCredential(roomId, body, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/credentials`;
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (options?.adminToken !== undefined && options.adminToken !== null) {
        headers.authorization = `Bearer ${options.adminToken}`;
      }
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            loginId: body.loginId,
            password: body.password,
            ...(body.displayName !== undefined ? { displayName: body.displayName } : {}),
            permissions: body.permissions,
          }),
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('createCredential: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      // server は **201 Created** を返す (POST 共通流儀ではないが Hono の handler
      // 仕様に従う). 200 も forward-compat で受ける.
      if (res.status === 201 || res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('createCredential: 201 with invalid JSON body');
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        if (!isCredentialSummary(parsed)) {
          logger.warn('createCredential: 201 body shape mismatch', { parsed });
          return { outcome: 'unknown', message: 'invalid response body' };
        }
        return { outcome: 'ok', credential: parsed };
      }
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 409) return { outcome: 'conflict' };
      if (res.status === 429) {
        const retryAfterMs = await parseRetryAfter(res);
        return { outcome: 'rate-limited', retryAfterMs };
      }
      logger.warn('createCredential: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async deleteCredential(roomId, credId, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}/credentials/${encodeURIComponent(credId)}`;
      const headers: Record<string, string> = {};
      if (options?.adminToken !== undefined && options.adminToken !== null) {
        headers.authorization = `Bearer ${options.adminToken}`;
      }
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'DELETE',
          headers,
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('deleteCredential: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 204) return { outcome: 'ok' };
      // 200 forward-compat.
      if (res.status === 200) return { outcome: 'ok' };
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 404) return { outcome: 'not-found' };
      logger.warn('deleteCredential: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },

    async lookupRoomsByTokens(entries) {
      const path = `${baseUrl}/admin/rooms/by-tokens`;
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // entries は readonly array なので spread でただの array に. server 側
          // schema は `{ entries: [...] }` strict なので余分なフィールド送らない.
          body: JSON.stringify({
            entries: entries.map((e) => ({ roomId: e.roomId, adminToken: e.adminToken })),
          }),
          credentials: 'include',
        });
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        logger.warn('lookupRoomsByTokens: network error', { err: message });
        return { outcome: 'network-error', message };
      }

      if (res.status === 200) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          logger.warn('lookupRoomsByTokens: 200 with invalid JSON body');
          return { outcome: 'network-error', message: 'invalid response body' };
        }
        if (typeof parsed !== 'object' || parsed === null) {
          return { outcome: 'network-error', message: 'invalid response body' };
        }
        const r = parsed as Record<string, unknown>;
        if (!Array.isArray(r.active) || !Array.isArray(r.deleted) || !Array.isArray(r.invalid)) {
          return { outcome: 'network-error', message: 'invalid response body' };
        }
        // row 単位 gate (forward-compat: 不正 row は黙って skip).
        const active: ActiveRoomLookupResult[] = [];
        for (const row of r.active) {
          if (!isActiveLookupRow(row)) {
            logger.warn('lookupRoomsByTokens: skipping malformed active row', { row });
            continue;
          }
          active.push(row);
        }
        const deleted: DeletedRoomLookupResult[] = [];
        for (const row of r.deleted) {
          if (!isDeletedLookupRow(row)) {
            logger.warn('lookupRoomsByTokens: skipping malformed deleted row', { row });
            continue;
          }
          deleted.push(row);
        }
        const invalid: string[] = [];
        for (const row of r.invalid) {
          if (typeof row === 'string' && row.length > 0) invalid.push(row);
        }
        return { outcome: 'ok', active, deleted, invalid };
      }
      if (res.status === 400) return { outcome: 'invalid-body' };
      if (res.status === 429) return { outcome: 'rate-limited' };
      logger.warn('lookupRoomsByTokens: unexpected http status', { status: res.status });
      return { outcome: 'network-error', message: `HTTP ${res.status}` };
    },

    async deleteRoom(roomId, options) {
      const path = `${baseUrl}/admin/rooms/${encodeURIComponent(roomId)}`;
      const headers: Record<string, string> = {};
      if (options?.adminToken !== undefined && options.adminToken !== null) {
        headers.authorization = `Bearer ${options.adminToken}`;
      }
      let res: Response;
      try {
        res = await fetchFn(path, {
          method: 'DELETE',
          headers,
          credentials: 'include',
        });
      } catch (cause) {
        logger.warn('deleteRoom: network error', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return { outcome: 'unknown', message: 'network error' };
      }

      if (res.status === 204) return { outcome: 'ok' };
      if (res.status === 200) return { outcome: 'ok' };
      if (res.status === 401) return { outcome: 'unauthorized' };
      if (res.status === 404) return { outcome: 'not-found' };
      logger.warn('deleteRoom: unexpected http status', { status: res.status });
      return { outcome: 'unknown', message: `HTTP ${res.status}` };
    },
  };
};
